// src/components/BusinessInsights.jsx
import { Fragment } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { DEFAULT_RUN_DOMAIN, getDomainProfile } from "../config/domainProfiles";

/** Result and impact only; problem / model / decision blocks are not shown in the UI. */
const INSIGHT_SECTION_ORDER = ["result", "impact"];

/** Insight cards grouped under `labels.insightPilotResultGroupTitle` (order within INSIGHT_SECTION_ORDER). */
const PILOT_RESULT_INSIGHT_GROUP_KEYS = /** @type {const} */ (["result", "impact"]);

/**
 * @param {{ key: string }[]} orderedSections
 * @param {number} index
 * @param {string} sectionKey
 * @param {unknown} titleRaw
 */
function shouldShowPilotResultGroupHeading(orderedSections, index, sectionKey, titleRaw) {
  const title = typeof titleRaw === "string" ? titleRaw.trim() : "";
  if (!title || !PILOT_RESULT_INSIGHT_GROUP_KEYS.includes(sectionKey)) return false;
  for (let j = 0; j < index; j += 1) {
    if (PILOT_RESULT_INSIGHT_GROUP_KEYS.includes(orderedSections[j].key)) return false;
  }
  return true;
}

/** @param {ReturnType<typeof getDomainProfile>} labels */
function useInsightSections(labels) {
  const theme = useTheme();
  const p = theme.palette;
  const byKey = {
    result: { key: "result", label: labels.insightSectionResult, Icon: BarChartIcon, color: p.success.main, bg: p.success.light },
    impact: { key: "impact", label: labels.insightSectionImpact, Icon: TrendingUpIcon, color: p.primary.dark, bg: p.primary.light },
  };
  return INSIGHT_SECTION_ORDER.map((k) => byKey[k]);
}

function ResultContent({ data }) {
  return (
    <Stack spacing={1}>
      {data.headline && <Typography variant="h6" fontWeight={600}>{data.headline}</Typography>}
      {data.metrics?.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {data.metrics.map((m) => (
            <Chip
              key={m.label}
              label={`${m.label}: ${m.value}`}
              size="small"
              color={m.good === true ? "success" : m.good === false ? "error" : "default"}
              variant="outlined"
            />
          ))}
        </Box>
      )}
      {data.chart_html && (
        <Box sx={{ overflowX: "auto", mt: 1 }} dangerouslySetInnerHTML={{ __html: data.chart_html }} />
      )}
    </Stack>
  );
}

function ImpactContent({ data }) {
  const { status } = data;
  if (status === "pending" || status === "running") {
    return (
      <Box display="flex" alignItems="center" gap={1.5} py={1}>
        <CircularProgress size={20} />
        <Typography variant="body1" color="text.secondary">Impact simulation in progress…</Typography>
      </Box>
    );
  }
  if (status === "failed") {
    return <Alert severity="error">{data.error || "Impact simulation failed."}</Alert>;
  }
  return (
    <Stack spacing={1}>
      {data.headline && <Typography variant="h6" fontWeight={600}>{data.headline}</Typography>}
      {data.metrics?.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {data.metrics.map((m) => (
            <Chip key={m.label} label={`${m.label}: ${m.value}`} size="small" variant="outlined" />
          ))}
        </Box>
      )}
      {data.details && (
        Array.isArray(data.details) ? (
          <List dense sx={{ py: 0 }}>
            {data.details.map((detail, index) => (
              <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                <ListItemText 
                  primary={detail} 
                  primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">{data.details}</Typography>
        )
      )}
      {data.chart_html && (
        <Box sx={{ overflowX: "auto", mt: 1 }} dangerouslySetInnerHTML={{ __html: data.chart_html }} />
      )}
    </Stack>
  );
}

const RENDERERS = { result: ResultContent, impact: ImpactContent };

function InsightCard({ section, data }) {
  const { label, Icon, color, bg } = section;
  const Renderer = RENDERERS[section.key];
  const showHeading = typeof label === "string" && label.trim().length > 0;
  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${color}` }}>
      <CardContent>
        {showHeading ? (
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon sx={{ fontSize: 18, color }} />
            </Box>
            <Typography variant="overline" sx={{ color, fontWeight: 700, lineHeight: 1 }}>{label}</Typography>
          </Box>
        ) : null}
        <Renderer data={data} />
      </CardContent>
    </Card>
  );
}

/** Empty shell card (block layout): same frame, placeholder body. */
function InsightShellEmptyCard({ section, labels }) {
  const { label, Icon, color, bg } = section;
  const emptyLine = labels.insightShellEmptyLine ?? "No data yet.";
  const showHeading = typeof label === "string" && label.trim().length > 0;
  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${color}` }}>
      <CardContent>
        {showHeading ? (
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon sx={{ fontSize: 18, color }} />
            </Box>
            <Typography variant="overline" sx={{ color, fontWeight: 700, lineHeight: 1 }}>{label}</Typography>
          </Box>
        ) : null}
        <Typography variant="body2" color="text.secondary">{emptyLine}</Typography>
      </CardContent>
    </Card>
  );
}

function SkeletonCard() {
  return (
    <Card variant="outlined">
      <CardContent>
        <Skeleton width={120} height={20} sx={{ mb: 1.5 }} />
        <Skeleton width="60%" height={28} sx={{ mb: 1 }} />
        <Skeleton width="90%" />
        <Skeleton width="75%" />
      </CardContent>
    </Card>
  );
}

/**
 * @param {{ insights: object | null; loading: boolean; domainProfile?: ReturnType<typeof getDomainProfile> }} props
 */
export default function BusinessInsights({ insights, loading, domainProfile: domainProfileProp }) {
  const labels = domainProfileProp ?? getDomainProfile(DEFAULT_RUN_DOMAIN);
  const SECTIONS = useInsightSections(labels);
  const rich = labels.layoutVariant === "cpWealthRich";

  if (loading) {
    return <Stack spacing={2}>{SECTIONS.map((s) => <SkeletonCard key={s.key} />)}</Stack>;
  }

  const visibleSections = SECTIONS.filter((s) => insights?.[s.key] != null);

  if (!insights || visibleSections.length === 0) {
    if (!rich) {
      const pilotTitle = labels.insightPilotResultGroupTitle;
      return (
        <Stack spacing={2}>
          {SECTIONS.map((s, i) => {
            const showPilotHeading = shouldShowPilotResultGroupHeading(SECTIONS, i, s.key, pilotTitle);
            const headingText = typeof pilotTitle === "string" ? pilotTitle.trim() : "";
            return (
              <Fragment key={s.key}>
                {showPilotHeading ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700} component="h3">
                      {headingText}
                    </Typography>
                    <InsightShellEmptyCard section={s} labels={labels} />
                  </Stack>
                ) : (
                  <InsightShellEmptyCard section={s} labels={labels} />
                )}
              </Fragment>
            );
          })}
        </Stack>
      );
    }
    return <Alert severity="info">{labels.insightsUnavailableMessage}</Alert>;
  }

  const pilotTitle = labels.insightPilotResultGroupTitle;

  return (
    <Stack spacing={2}>
      {visibleSections.map((s, i) => {
        const showPilotHeading = shouldShowPilotResultGroupHeading(visibleSections, i, s.key, pilotTitle);
        const headingText = typeof pilotTitle === "string" ? pilotTitle.trim() : "";
        return (
          <Fragment key={s.key}>
            {showPilotHeading ? (
              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={700} component="h3">
                  {headingText}
                </Typography>
                <InsightCard section={s} data={insights[s.key]} />
              </Stack>
            ) : (
              <InsightCard section={s} data={insights[s.key]} />
            )}
          </Fragment>
        );
      })}
    </Stack>
  );
}
